import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "@/hooks/use-toast";
import usePermissions from "@/hooks/usePermissions";
import { apiRequest } from "@/lib/queryClient";
import { organizationSaveErrorMessage, throwIfProblemDetails } from "@/hooks/useOrganizations";
import {
  APP_PACKAGE_OPTIONS,
  ATHLYN_APP_PACKAGE,
  AppPackageFields,
  OTHER_APP_PACKAGE,
  appPackageErrors,
  appPackageValueFrom,
  normalizePackage,
  selectedPackage,
  type AppPackageValue,
} from "@/components/corporate/organizations/AppPackageFields";
import {
  SUBDOMAIN_FORMAT_ERROR,
  SUBDOMAIN_LOCKED_NOTE,
  SUBDOMAIN_PATTERN,
  SUBDOMAIN_REQUIRED_ERROR,
  normalizeSubdomain,
  subdomainChangeWarnings,
} from "@/components/corporate/organizations/subdomainRules";
import { appDisplayName, portalAddress } from "@/components/corporate/organizations/orgOptions";
import { AdminOnlyNote, ReadOnlyRow, SaveBar, SectionFrame } from "@/components/corporate/organizations/page/SectionFrame";
import { useReportDirty, useSectionDraft } from "@/components/corporate/organizations/page/useSectionDraft";
import { invalidateOrganization } from "@/components/corporate/organizations/page/orgSave";
import type { Organization } from "@/types/organizations";
import type {
  AppActivePhonesResponse,
  UpdateOrganizationAppRequest,
} from "@/types/generated/cms.generated";

interface AppAddressSectionProps {
  organization: Organization;
  onDirtyChange?: (dirty: boolean) => void;
}

interface AppAddressDraft {
  subdomain: string;
  appPackage: AppPackageValue;
}

type FieldErrors = { subdomain?: string; appScheme?: string; appStoreId?: string };

const sameDraft = (a: AppAddressDraft, b: AppAddressDraft) =>
  a.subdomain === b.subdomain &&
  a.appPackage.choice === b.appPackage.choice &&
  a.appPackage.other === b.appPackage.other &&
  a.appPackage.appStoreId === b.appPackage.appStoreId;

const fromOrganization = (o: Organization): AppAddressDraft => ({
  subdomain: o.subdomain ?? "",
  appPackage: appPackageValueFrom(o.appScheme, o.appStoreId, o.isKnownApp === true),
});

/** "<n> phone(s) use(s) the current app. …" — the W2 move warning. */
export function appMoveWarning(count: number, appName: string): string {
  const phones = count === 1 ? "1 phone uses" : `${count} phones use`;
  return `${phones} the current app. After the move they get no notifications until each person installs ${appName} and signs in. Every phone must update.`;
}

/** The select's label for a package ("Athlyn (com.athlyn.app)"), else the package itself. */
const packageLabel = (pkg: string | null | undefined) => {
  const value = normalizePackage(pkg);
  return APP_PACKAGE_OPTIONS.find((o) => o.value === value)?.label ?? (value || "No app set");
};

interface PendingMove {
  count: number;
  appName: string;
  subdomain: string | null;
}

/**
 * Settings → App & web address (Athlyn admins only, OP-D7). The subdomain (W1) and the app package +
 * App Store ID (W2), saved through the admin-only `PUT organizations/{id}/subdomain` and
 * `PUT organizations/{id}/app` — at most two calls, subdomain first. A package change first counts
 * the phones on the current app and, with any, asks to confirm the move. Team members read only.
 */
export function AppAddressSection({ organization, onDirtyChange }: AppAddressSectionProps) {
  const { isAthlynAdmin } = usePermissions();
  if (!isAthlynAdmin) return <AppAddressReadOnly organization={organization} />;
  return <AppAddressEditor organization={organization} onDirtyChange={onDirtyChange} />;
}

function AppAddressReadOnly({ organization }: { organization: Organization }) {
  return (
    <SectionFrame section="app" title="App & web address" adminOnly hint="The company's web address and the app its employees install.">
      <dl className="space-y-2.5">
        <ReadOnlyRow label="Web address" value={portalAddress(organization.subdomain)} />
        <ReadOnlyRow label="App" value={packageLabel(organization.appScheme)} />
        <ReadOnlyRow label="App Store ID" value={organization.appStoreId?.trim() || "Not set"} />
      </dl>
      <AdminOnlyNote />
    </SectionFrame>
  );
}

function AppAddressEditor({ organization, onDirtyChange }: AppAddressSectionProps) {
  const queryClient = useQueryClient();
  const draft = useSectionDraft(fromOrganization(organization), sameDraft);
  const { values, base } = draft;
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [pendingSubdomain, setPendingSubdomain] = useState<string | null>(null);
  const [pendingMove, setPendingMove] = useState<PendingMove | null>(null);
  const [saving, setSaving] = useState(false);
  const submittingRef = useRef(false);
  useReportDirty(onDirtyChange, draft.dirty);

  const savedPackage = selectedPackage(base.appPackage) ?? "";
  const knownPackage = organization.isKnownApp ? normalizePackage(organization.appScheme) : null;
  // The subdomain may change only while the company stays on the Athlyn app: choosing another app
  // locks it for this save, so /subdomain (which checks the stored package) never renames a
  // company that is about to leave the Athlyn app.
  const keepsAthlynFlags = (value: AppPackageValue) => {
    const pkg = selectedPackage(value);
    if (!pkg) return value.choice !== OTHER_APP_PACKAGE; // "Other…" not typed yet: already another app
    return pkg === savedPackage || pkg === ATHLYN_APP_PACKAGE;
  };
  const canChangeSubdomain = organization.canChangeSubdomain === true && keepsAthlynFlags(values.appPackage);

  const nextSubdomain = normalizeSubdomain(values.subdomain);
  const subdomainChanged = canChangeSubdomain && nextSubdomain !== normalizeSubdomain(base.subdomain);
  const pkg = selectedPackage(values.appPackage);
  const packageChanged = pkg !== null && pkg !== savedPackage;
  const storeIdChanged = values.appPackage.appStoreId.trim() !== base.appPackage.appStoreId.trim();
  const appChanged = packageChanged || storeIdChanged;
  const otherNotTyped = values.appPackage.choice === OTHER_APP_PACKAGE && pkg === null;
  const canSave = subdomainChanged || appChanged || otherNotTyped;

  const setPackage = (value: AppPackageValue) => {
    if (!keepsAthlynFlags(value)) {
      // Another app locks the subdomain for this save: drop any typed rename.
      draft.setValues((prev) => ({ ...prev, appPackage: value, subdomain: base.subdomain }));
      setFieldErrors(({ subdomain: _sub, appScheme: _scheme, appStoreId: _storeId, ...rest }) => rest);
    } else {
      draft.set("appPackage", value);
      setFieldErrors(({ appScheme: _scheme, appStoreId: _storeId, ...rest }) => rest);
    }
  };

  const failToast = (error: unknown, fallback: string, prefix = "") =>
    toast({
      title: "Error",
      description: `${prefix}${organizationSaveErrorMessage(error, fallback)}`,
      variant: "destructive",
    });

  /** The calls, in order: subdomain, then the app. Nothing is retried that already went through. */
  const write = async (newSubdomain: string | null, confirmPhonesMustUpdate: boolean) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSaving(true);
    try {
      let subdomainSaved = false;
      if (newSubdomain) {
        try {
          throwIfProblemDetails(await apiRequest("PUT", `/organizations/${organization.id}/subdomain`, { subdomain: newSubdomain }));
        } catch (error) {
          failToast(error, "Couldn't change the web address. Please try again.");
          return;
        }
        subdomainSaved = true;
        // The new address is saved: a retry after a refused app change renames nothing again.
        draft.markSaved({ subdomain: values.subdomain, appPackage: base.appPackage });
        invalidateOrganization(queryClient, organization.id);
      }

      if (appChanged) {
        const body: UpdateOrganizationAppRequest = { appScheme: pkg ?? savedPackage };
        // On a package change the server clears an App Store ID that is not sent, so the ID the field
        // shows always goes with it ("" clears it; picking a known app has already emptied the field).
        if (storeIdChanged || packageChanged) body.appStoreId = values.appPackage.appStoreId.trim();
        if (confirmPhonesMustUpdate) body.confirmPhonesMustUpdate = true;
        try {
          throwIfProblemDetails(await apiRequest("PUT", `/organizations/${organization.id}/app`, body));
        } catch (error) {
          failToast(
            error,
            "Couldn't change the app. Please try again.",
            subdomainSaved ? "The web address was saved; the app was not. " : "",
          );
          return;
        }
      }

      draft.markSaved(values);
      invalidateOrganization(queryClient, organization.id);
      toast({ title: "App & web address saved", description: "The changes are saved." });
    } finally {
      submittingRef.current = false;
      setSaving(false);
    }
  };

  /** After the subdomain confirm (if any): count the phones before a package change. */
  const continueSave = async (newSubdomain: string | null) => {
    if (!packageChanged) {
      await write(newSubdomain, false);
      return;
    }
    let phones: AppActivePhonesResponse;
    try {
      phones = await apiRequest<AppActivePhonesResponse>("GET", `/organizations/${organization.id}/app/active-phones`);
    } catch (error) {
      failToast(error, "Couldn't count the phones on the current app. Please try again.");
      return;
    }
    const count = phones?.count ?? 0;
    if (count > 0) {
      setPendingMove({ count, appName: appDisplayName(pkg), subdomain: newSubdomain });
      return;
    }
    await write(newSubdomain, false);
  };

  const onSave = async () => {
    if (submittingRef.current || saving) return;
    const errors: FieldErrors = { ...appPackageErrors(values.appPackage, knownPackage) };
    if (subdomainChanged) {
      if (!nextSubdomain) errors.subdomain = SUBDOMAIN_REQUIRED_ERROR;
      else if (!SUBDOMAIN_PATTERN.test(nextSubdomain)) errors.subdomain = SUBDOMAIN_FORMAT_ERROR;
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast({ title: "Validation Error", description: "Fix the highlighted fields.", variant: "destructive" });
      return;
    }
    if (subdomainChanged) {
      // Nothing is saved until the confirm says so; Cancel sends nothing.
      setPendingSubdomain(nextSubdomain);
      return;
    }
    await continueSave(null);
  };

  const subdomainDescribedBy = !canChangeSubdomain
    ? "org-app-subdomain-locked"
    : fieldErrors.subdomain
      ? "org-app-subdomain-error"
      : "org-app-subdomain-help";

  return (
    <SectionFrame
      section="app"
      title="App & web address"
      adminOnly
      hint="The company's web address and the app its employees install."
      footer={
        <SaveBar
          dirty={canSave}
          saving={saving}
          onSave={() => void onSave()}
          onCancel={() => {
            draft.discard();
            setFieldErrors({});
          }}
          note="A new web address and a move to another app each ask you to confirm first."
        />
      }
    >
      <div className="max-w-md space-y-1.5">
        <Label htmlFor="org-app-subdomain">Subdomain</Label>
        <Input
          id="org-app-subdomain"
          value={values.subdomain}
          onChange={(e) => {
            draft.set("subdomain", e.target.value);
            setFieldErrors(({ subdomain: _cleared, ...rest }) => rest);
          }}
          readOnly={!canChangeSubdomain}
          autoCapitalize="none"
          spellCheck={false}
          maxLength={100}
          className={!canChangeSubdomain ? "bg-ds-sunk text-ds-ink3" : undefined}
          aria-invalid={!!fieldErrors.subdomain}
          aria-describedby={subdomainDescribedBy}
        />
        {fieldErrors.subdomain && (
          <p id="org-app-subdomain-error" className="text-sm text-ds-err">
            {fieldErrors.subdomain}
          </p>
        )}
        {canChangeSubdomain ? (
          <p id="org-app-subdomain-help" className="text-xs text-ds-ink3">
            {/* Only a valid name is previewed; anything else shows the current address. */}
            Portal address:{" "}
            {portalAddress(SUBDOMAIN_PATTERN.test(nextSubdomain) ? nextSubdomain : base.subdomain)}
          </p>
        ) : (
          <p id="org-app-subdomain-locked" className="text-xs text-ds-ink3">
            {SUBDOMAIN_LOCKED_NOTE}
          </p>
        )}
      </div>

      <AppPackageFields
        idPrefix="org-app"
        value={values.appPackage}
        onChange={setPackage}
        errors={{ appScheme: fieldErrors.appScheme, appStoreId: fieldErrors.appStoreId }}
        extraKnownPackage={knownPackage}
      />

      <AlertDialog open={pendingSubdomain !== null} onOpenChange={(open) => !open && setPendingSubdomain(null)}>
        <AlertDialogContent data-testid="subdomain-change-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Change the subdomain to {pendingSubdomain}?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <ul className="list-disc space-y-1 pl-5 text-left text-sm text-muted-foreground">
                {subdomainChangeWarnings(pendingSubdomain ?? "").map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const next = pendingSubdomain;
                setPendingSubdomain(null);
                if (next) void continueSave(next);
              }}
            >
              Change subdomain
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={pendingMove !== null} onOpenChange={(open) => !open && setPendingMove(null)}>
        <AlertDialogContent data-testid="app-move-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Move this company to {pendingMove?.appName}?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingMove ? appMoveWarning(pendingMove.count, pendingMove.appName) : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const move = pendingMove;
                setPendingMove(null);
                if (move) void write(move.subdomain, true);
              }}
            >
              Move to {pendingMove?.appName}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SectionFrame>
  );
}

export default AppAddressSection;
